import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  fetchSloCollectionCurrent,
  fetchSloCollectionTrend,
  fetchSloSnapshotToday,
} from '../../api/client';
import {
  MiniLineChart,
  StatusGrid,
  HeatmapGrid,
  ProjectionTable,
  Section,
  MetricsLegend,
  TenantIndicator,
  StackedBar,
  formatLatency,
  formatLatencyMinutes,
  formatRelativeMinutes,
} from './sloShared';
import Badge from '../ui/Badge';

const POLL_INTERVAL_MS = 30000;

const COLLECTION_LEGEND = [
  { term: 'Our processing time', defn: 'pollToUploadSec — seconds from when the worker started polling Microsoft to when the blob landed in bronze-staging. This is what we control and what the SLO alert fires on.' },
  { term: 'Microsoft + cadence', defn: 'eventToPollSec — seconds from when the M365 event happened to when our worker began polling. Combines Microsoft indexing delay and our deliberate poll cadence (30 min). Reported only — not alerted on.' },
  { term: 'Total user-visible', defn: 'eventToUploadSec — sum of the above. The end-user-perceived freshness lag.' },
  { term: 'p50 (median)', defn: 'Half of records are at or below this value. The "typical" experience.' },
  { term: 'p95', defn: '95% of records are at or below this. Catches almost all real-world latency, ignores extreme outliers.' },
  { term: 'p99', defn: '99% of records are at or below this. The "tail" — the SLO is measured on this.' },
];

export default function CollectionTab({ active, tenantId = '', tenantLookup = {} }) {
  const [current, setCurrent] = useState(null);
  const [trend, setTrend] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [displayedTenantId, setDisplayedTenantId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadCurrent = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const data = await fetchSloCollectionCurrent({ tenantId });
      setCurrent(data);
      setDisplayedTenantId(tenantId);
      setError('');
    } catch (err) {
      console.error('[CollectionTab] current fetch failed', err);
      setError(err?.message || 'Failed to load collection SLO data.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [tenantId]);

  const loadTrend = useCallback(async () => {
    try {
      const data = await fetchSloCollectionTrend({ days: 14 });
      setTrend(data);
    } catch (err) {
      console.error('[CollectionTab] trend fetch failed', err);
    }
  }, []);

  const loadSnapshot = useCallback(async () => {
    try {
      const data = await fetchSloSnapshotToday();
      setSnapshot(data);
    } catch (err) {
      console.error('[CollectionTab] snapshot fetch failed', err);
    }
  }, []);

  useEffect(() => {
    if (!active) return undefined;
    loadCurrent(false);
    loadTrend();
    loadSnapshot();
    const id = setInterval(() => loadCurrent(true), POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [active, loadCurrent, loadTrend, loadSnapshot]);

  const targetSec = current?.targetSec ?? trend?.targetSec ?? 600;
  const targetMin = (targetSec / 60).toFixed(1);

  const workloadCols = useMemo(() => [
    { key: 'samples',      label: 'Samples',       align: 'right' },
    { key: 'compliantPct', label: 'Compliant %',   align: 'right',
      fmt: (v) => v == null ? '—' : `${Number(v).toFixed(2)}%` },
    { key: 'avgSec',       label: 'avg',           align: 'right',
      fmt: (v) => formatLatency(v) },
    { key: 'p50Sec',       label: 'p50',           align: 'right',
      fmt: (v) => formatLatency(v) },
    { key: 'p95Sec',       label: 'p95',           align: 'right',
      fmt: (v) => formatLatency(v) },
    { key: 'p99Sec',       label: 'p99 (SLO)',     align: 'right',
      fmt: (v) => formatLatency(v) },
    { key: 'maxSec',       label: 'Max',           align: 'right',
      fmt: (v) => formatLatency(v) },
  ], []);

  const heatmapWorkloads = useMemo(() => {
    if (!current?.tenants) return [];
    return Array.from(new Set(current.tenants.map((c) => c.workload))).sort();
  }, [current]);
  const heatmapTenants = useMemo(() => {
    if (!current?.tenants) return [];
    return Array.from(new Set(current.tenants.map((c) => c.tenantId))).sort();
  }, [current]);

  // Breakdown — sort by total user-visible p99 desc, capped to top 12 by latency
  const breakdownRows = useMemo(() => {
    if (!current?.breakdown) return [];
    return [...current.breakdown].sort((a, b) =>
      Number(b.totalUserVisibleP99Sec || 0) - Number(a.totalUserVisibleP99Sec || 0)
    );
  }, [current]);
  const breakdownMaxSec = useMemo(() => {
    if (!breakdownRows.length) return 0;
    return Math.max(...breakdownRows.map((r) => Number(r.totalUserVisibleP99Sec || 0)));
  }, [breakdownRows]);

  return (
    <div>
      {error && (
        <div className="card mb-4 bg-red-900/20 border-red-700/40 text-red-200 text-sm">
          {error}
        </div>
      )}

      <Section
        title="Processing-time SLO (last 1 hour)"
        subtitle={`Per-workload pollToUploadSec — the time *we* take from worker poll-start to bronze-staging blob written. SLO target: p99 ≤ ${targetSec}s (${targetMin} min). Microsoft indexing delay and our cadence wait are reported separately below.`}
        right={
          <div className="flex items-center gap-3">
            <TenantIndicator
              selectedTenantId={tenantId}
              displayedTenantId={displayedTenantId}
              tenantLookup={tenantLookup}
              loading={loading}
            />
            {current && (
              <div className="text-xs text-gray-500">
                {loading ? 'refreshing…' : `updated ${formatRelativeMinutes(current.generatedUtc)}`}
              </div>
            )}
            <MetricsLegend items={COLLECTION_LEGEND} />
          </div>
        }
      >
        <StatusGrid
          rows={current?.workloads || []}
          columns={workloadCols}
          emptyText={loading ? 'Loading…' : 'No collection-stage telemetry in the last hour.'}
        />
      </Section>

      <Section
        title="Total freshness breakdown"
        subtitle="Where the user-visible delay comes from. Gray = Microsoft indexing + our poll cadence (out of our control). Blue = our processing time (the SLO metric)."
      >
        {breakdownRows.length === 0 ? (
          <div className="text-sm text-gray-500 italic px-3 py-4">No breakdown data in the last hour.</div>
        ) : (
          <div className="space-y-2.5">
            {breakdownRows.map((row) => (
              <div key={row.workload} className="grid grid-cols-[160px_1fr] gap-3 items-center">
                <span className="font-mono text-xs text-gray-300">{row.workload}</span>
                <StackedBar
                  leftSec={row.microsoftPlusCadenceP99Sec}
                  rightSec={row.ourProcessingP99Sec}
                  leftLabel="MS + cadence p99"
                  rightLabel="Our p99"
                  maxSec={breakdownMaxSec}
                />
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section
        title="Per-tenant heatmap (processing time, last 1 hour)"
        subtitle="p99 pollToUploadSec by (workload × tenant). Green = ok, amber = approaching threshold, red = exceeded."
      >
        <HeatmapGrid
          cells={current?.tenants || []}
          workloads={heatmapWorkloads}
          tenants={heatmapTenants}
          tenantLookup={tenantLookup}
          valueKey="p99Sec"
          valueLabel="s"
        />
      </Section>

      <Section
        title="14-day p99 trend per workload"
        subtitle={`Daily p99 of pollToUploadSec. Dashed line = ${targetSec}-second SLO target.`}
      >
        <TrendCharts trend={trend} thresholdSec={targetSec} />
      </Section>

      <Section
        title="Days-until-exceeded projection"
        subtitle="Linear regression over the daily p99 series. Workloads about to exceed the SLO show first."
      >
        <ProjectionTable
          workloads={trend?.workloads || []}
          targetValue={targetSec / 60}
          targetUnit="min"
          currentField="currentP99Sec"
          projectedField="projectedP99In14d"
          valueFormatter={(v) => formatLatencyMinutes(v, 2)}
          slopeFormatter={(v) => v == null ? '—' : (Number(v) / 60).toFixed(3)}
        />
      </Section>

      <Section
        title="Daily snapshot health (today's 02:00–04:00 UTC window)"
        subtitle="Worker runs that must complete by 04:00 UTC each day."
      >
        <DailySnapshotTable snapshot={snapshot} />
      </Section>
    </div>
  );
}

function TrendCharts({ trend, thresholdSec }) {
  if (!trend || !trend.workloads || trend.workloads.length === 0) {
    return <div className="text-sm text-gray-500 italic px-3 py-4">No trend data yet.</div>;
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {trend.workloads.map((w) => {
        const series = (w.dailySeries || []).map((d) => ({ day: d.day, value: d.p99Sec }));
        return (
          <div key={w.workload} className="border border-gray-800 rounded-lg p-3 bg-gray-950/50">
            <div className="flex items-center justify-between mb-1">
              <span className="font-mono text-xs text-gray-300">{w.workload}</span>
              <span className="text-[11px] text-gray-500">
                {w.samples ?? 0} days obs · slope {Number(w.slope ?? 0).toFixed(2)}s/day
              </span>
            </div>
            <MiniLineChart series={series} threshold={thresholdSec} yLabel="s" height={70} />
          </div>
        );
      })}
    </div>
  );
}

function DailySnapshotTable({ snapshot }) {
  if (!snapshot) {
    return <div className="text-sm text-gray-500 italic px-3 py-4">Loading…</div>;
  }
  if (!snapshot.workers || snapshot.workers.length === 0) {
    return (
      <div className="text-sm text-gray-500 italic px-3 py-4">
        No worker runs in today's 02:00–04:00 UTC window yet.
      </div>
    );
  }
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left border-b border-gray-800">
          <th className="px-3 py-2 font-medium text-gray-400">Worker</th>
          <th className="px-3 py-2 font-medium text-gray-400 text-right">Total runs</th>
          <th className="px-3 py-2 font-medium text-gray-400 text-right">Successful</th>
          <th className="px-3 py-2 font-medium text-gray-400 text-right">Failed</th>
          <th className="px-3 py-2 font-medium text-gray-400 text-right">Latest completion (UTC)</th>
          <th className="px-3 py-2 font-medium text-gray-400 text-right">Margin to 04:00 UTC</th>
        </tr>
      </thead>
      <tbody>
        {snapshot.workers.map((w) => {
          const margin = Number(w.marginMin ?? 0);
          const tone = w.failedRuns > 0 ? 'critical'
                     : margin <= 0 ? 'critical'
                     : margin < 15 ? 'warning'
                     : 'success';
          return (
            <tr key={w.workerName} className="border-b border-gray-800/50">
              <td className="px-3 py-2 font-mono text-xs text-gray-300">{w.workerName}</td>
              <td className="px-3 py-2 text-right tabular-nums">{w.totalRuns}</td>
              <td className="px-3 py-2 text-right tabular-nums">{w.successfulRuns}</td>
              <td className="px-3 py-2 text-right tabular-nums">{w.failedRuns}</td>
              <td className="px-3 py-2 text-right text-xs text-gray-400 tabular-nums">
                {w.latestCompletion ? new Date(w.latestCompletion).toISOString().slice(11, 19) : '—'}
              </td>
              <td className="px-3 py-2 text-right">
                <Badge tone={tone}>
                  {margin == null ? '—' : margin >= 0 ? `${margin}m ahead` : `${Math.abs(margin)}m late`}
                </Badge>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
